import sortBy from "lodash/sortBy";
import {useState} from "react";
import {useFormContext} from "react-hook-form";

import {ContactsSelect} from "@/components/contactsSelect/ContactsSelect.tsx";
import {
  AnalyzerCreateFormValues,
  AnalyzerSettingsContactsFieldProps,
  AnalyzerSettingsLock,
} from "@/pages/analyzer/Analyzer.types.ts";
import {
  buildContactsLockedExample,
  hasDivergentOverrides,
  resolveContactGroupNames,
} from "@/pages/analyzer/Analyzer.utils.ts";
import {AnalyzerSettingsField} from "@/pages/analyzer/components/chat/AnalyzerSettingsField.tsx";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";

export const AnalyzerSettingsContactsField = ({disabled}: AnalyzerSettingsContactsFieldProps) => {
  const {formData, proposedChecks} = useAnalyzerContext();
  const {watch, setValue} = useFormContext<AnalyzerCreateFormValues>();
  const contactGroups = watch("contactGroups");
  const [draft, setDraft] = useState<number[] | null>(null);

  const groups = formData?.contactGroups.items ?? [];
  const selectedNames = resolveContactGroupNames(contactGroups, groups);

  const locked: AnalyzerSettingsLock | undefined = hasDivergentOverrides(
    proposedChecks,
    selectedNames,
    "contact_groups"
  )
    ? {
        title: "Contacts now vary across Checks.",
        example: buildContactsLockedExample(
          groups.map((group) => group.name),
          proposedChecks
        ),
      }
    : undefined;

  const draftIds = draft ?? contactGroups;

  const commitDraft = () => {
    if (draft) {
      setValue("contactGroups", draft, {shouldDirty: true});
    }
    setDraft(null);
  };

  return (
    <AnalyzerSettingsField
      label="Contacts"
      subText="Contacts applied to all proposed Checks."
      chips={selectedNames.map((name) => ({name}))}
      onRemove={(name) => {
        const group = groups.find((entry) => entry.name === name);
        setValue(
          "contactGroups",
          contactGroups.filter((id) => id !== group?.id),
          {shouldDirty: true}
        );
      }}
      editing={draft !== null}
      onEdit={() => setDraft(contactGroups)}
      onCancel={() => setDraft(null)}
      onSave={commitDraft}
      locked={locked}
      disabled={disabled}
    >
      <ContactsSelect
        isMulti
        autoFocus
        openMenuOnFocus
        options={sortBy(groups, (group) => group.name)}
        value={groups.filter((group) => draftIds.includes(group.id))}
        menuPlacement="top"
        maxMenuHeight={28 * 5}
        onChange={(value) => setDraft(value.map((group) => group.id))}
      />
    </AnalyzerSettingsField>
  );
};
